import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import ChevronLeftIcon from '../../../assets/icons/alert-plus/chevron-left.svg';
import AlertRequestsTable from '../AlertRequestsTable/AlertRequestsTable';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import useWebSocket from '../../../components/common/useWebSocket';
import DismissAlertIcon from '../../../assets/icons/alert-plus/x.svg';
import API from '../../../utils/API';
import commonFunctions from '../../../utils/commonFunctions';
import './AlertRequestsPage.css';

const AlertRequestsPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const websocket = useWebSocket();

  const { siteId, requestTemplate } = location.state || {};

  const handleBackClick = () => {
    navigate('/alert-plus/manage');
  };

  const [alertRequests, setAlertRequests] = useState([]);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [sortByCol, setSortByCol] = useState('name');
  const [sortByAsc, setSortByAsc] = useState(true);
  const [startIndex, setStartIndex] = useState(0);

  const tableWrapperRef = useRef();
  const tableRef = useRef();

  const dataMap = useMemo(() => {
    return { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
  }, []);

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndex,
    startIndex,
    pageSize: 20,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  useEffect(() => {
    if (!websocket) return;

    const handleWebSocketMessage = (message) => {
      const data = JSON.parse(message.data);
      if (data.messageId === 5001) {
        loadTable(dataMap, false);
      }
    };

    websocket.addEventListener('message', handleWebSocketMessage);

    return () => {
      websocket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [websocket]);

  const loadTable = (map, resetSelection = true) => {
    // Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    // Fetch alert requests data
    API.getSiteAlerts(siteId)
      .then((response) => {
        const requestsData = response.data.requests;
        // console.log('requests:', requestsData);
        requestsData.forEach((request) => {
          // Only add requests for the current alert template
          if (request.alertTemplateId !== requestTemplate.id) return;
          // console.log('request:', request);
          map.data[request.alertRequestId] = {
            id: request.alertRequestId,
            templateId: request.alertTemplateId,
            description: request.description,
            requesterId: request.requesterId,
            requesterName: request.requesterName,
            requesterPhone: request.requesterPhone,
            timestamp: request.timestamp,
          };
        });
        map.filteredIds = Object.keys(map.data);
        map.ids = Object.keys(map.data);
        // console.log("map.data", map.data);
        setAlertRequests(requestsData);
        setDataCount(map.ids.length);
        setTotalDataCount(map.ids.length);
        setDataLoaded(true);
      })
      .catch((error) => {
        console.error('Error fetching alert requests:', error);
      });
  };

  useEffect(() => {
    loadTable(dataMap);
  }, [siteId]);

  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, alertRequests]);

  const tableColumnArray = [
    {
      Header: 'Name',
      accessor: 'requesterName',
    },
    {
      Header: 'Phone Number',
      accessor: 'requesterPhone',
    },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ value }) => (
        <div>{commonFunctions.formatTextForDisplay(value)}</div>
      ),
    },
    {
      Header: 'Initiated',
      accessor: 'timestamp',
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },
  ];

  const tableColumns = useMemo(() => tableColumnArray, []);

  const tableProps = {
    headerTitle: "REQUESTS",
    columns: tableColumns,
    data,
    dataCount,
    totalDataCount,
    dataLoaded,
    generateDataSlice,
    sortMap,
    setSortBy,
    sortByCol,
    sortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    startIndex,
  };

  const handleTriggerAlert = async (alertTemplateId) => {
    console.log('Triggering alert:', siteId, alertTemplateId)

    try {
      await API.triggerAlert([siteId], alertTemplateId);
      navigate('/alert-plus/manage', { state: { success: 'ALERT_TRIGGER_SUCCESS' } });
    } catch (error) {
      console.error('Error triggering alert:', error);
    }
  };

  const handleDismissAlert = async (alertTemplateId) => {
    console.log('Dismissing alert:', siteId, alertTemplateId);

    try {
      await API.dismissAlert(siteId, alertTemplateId);
      navigate('/alert-plus/manage', { state: { success: 'ALERT_DISMISS_SUCCESS' } });
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  return (
    <div className="alert-requests-page">
      <div className="back-button-container">
        <div className="back-button" onClick={handleBackClick}>
          <img src={ChevronLeftIcon} className="back-icon" alt="back-icon" />
          <FormattedMessage id="BACK" defaultMessage="Back" />
        </div>
      </div>
      <div className="alert-header">
        <div className="alert-info-left" style={{ color: requestTemplate.background_color }}>
          {requestTemplate.icon && <img src={requestTemplate.icon} className="alert-icon" alt={`${requestTemplate.name} icon`} />}
          <span className="alert-type">{requestTemplate.name}</span>
          &nbsp;<FormattedMessage id="ALERT_REQUESTS" />
        </div>
        <div className="alert-buttons">
          <button className="trigger-alert-button" onClick={() => handleTriggerAlert(requestTemplate.id)}>
            <FormattedMessage id="TRIGGER_ALERT" />
          </button>
          <button className="dismiss-alert-button" onClick={() => handleDismissAlert(requestTemplate.id)}>
            <img src={DismissAlertIcon} alt="Dismiss Alert" className="dismiss-alert-icon" />
          </button>
        </div>
      </div>
      <div className="alert-content">
        <AlertRequestsTable {...tableProps} />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(AlertRequestsPage);
